import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { fetchDetailedStaffInformation } from 'pages/AnnualUserAccessReview/_api';
import { STATUS_TYPES } from 'pages/AnnualUserAccessReview/_const';
import { isDateWithinNDays } from 'pages/AnnualUserAccessReview/_util';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';

/**
 * Higher-order component to inject `setIsLoading` function into wrapped component.
 * Renders either the component or a Skeleton loading animation.
 * @param {React.ReactNode} WrappedComponent component to be injected
 * @returns wrapped version of `WrappedComponent`
 */
const withQueryCheck = WrappedComponent => {
	const HOC = props => {
		const location = useLocation();
		const idsFromParams = new URLSearchParams(location.search).get(
			'esoFiId'
		);
		const idFromParams = idsFromParams
			?.split(',')
			.map(id => {
				const num = Number(id);
				if (isNaN(num)) {
					return undefined;
				}
				return num;
			})
			.filter(id => typeof id === 'number');
		const [sValuesFromParams, setValuesFromParams] = useState([]);
		const [sLoading, setLoading] = useState(true);

		useEffect(() => {
			let active = true;

			async function fetchApi() {
				const urls = idFromParams?.map(
					id => () => fetchDetailedStaffInformation(id)
				);
				if (urls?.length > 0) {
					axios.all(urls.map(url => url())).then(
						axios.spread((...responses) => {
							setValuesFromParams(
								responses.map(dataset => {
									const { expiresSoon, difference } =
										isDateWithinNDays(
											new Date(
												dataset.dateValidationExpires
											),
											30
										);

									const compliance = dataset?.isCompliant
										? expiresSoon
											? STATUS_TYPES.VALID_EXPIRING_SOON
											: STATUS_TYPES.VALID
										: STATUS_TYPES.OVERDUE;

									return {
										...dataset,
										isSelected: false,
										id: dataset.fiId,
										name: dataset.lastName
											? dataset.lastName +
											  ', ' +
											  dataset.firstName
											: null,
										status: compliance,
										daysUntilExpiration: difference,
										department: dataset.departmentName,
										jobTitle: dataset.jobTitle,
									};
								})
							);
							setLoading(false);
						})
					);
				} else {
					setLoading(false);
				}
			}

			if (active && sLoading) {
				fetchApi();
			}

			return () => {
				active = false;
			};
		}, [idFromParams, sLoading]);

		return (
			<WithLoadingHandler isLoading={sLoading} showChildren={!sLoading}>
				<WrappedComponent
					{...props}
					initialValuesFromParams={sValuesFromParams}
				/>
			</WithLoadingHandler>
		);
	};
	return HOC;
};

export default withQueryCheck;
